import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Star, Zap, ShieldCheck, Search, Target, Activity } from "lucide-react";
import { servicesContent } from "@/data/servicesContent";

const geoSlugs = ["geo-authority", "geo-enterprise", "geo-shield"];

const engines = [
  { name: "ChatGPT", org: "OpenAI" },
  { name: "Claude", org: "Anthropic" },
  { name: "Gemini", org: "Google" },
  { name: "Perplexity", org: "Perplexity AI" },
];

const stages = [
  { n: "01", title: "Baseline Audit", desc: "50+ prompt queries mapping current visibility, hallucinations, and every competitor citation across 4 major LLMs." },
  { n: "02", title: "Schema & Entity Alignment", desc: "Advanced JSON-LD deployed. Wikidata, Google Knowledge Graph, and Crunchbase entries aligned to your brand." },
  { n: "03", title: "Citation-Grade Content", desc: "Long-form authority articles engineered for LLM retrieval, not blog skimming. Digital PR seeded into trusted third-party sources." },
  { n: "04", title: "RAG Optimization", desc: "Perplexity + ChatGPT Search retrieval triggers optimized. Hallucination remediation cycle executed. Real-time citations tracked." },
  { n: "05", title: "Scorecard & Handoff", desc: "90-day AI Visibility Scorecard with quantified before/after evidence — the artifact your CMO shares with the board." },
];

export default function GeoDominance() {
  return (
    <div className="bg-[#0B0C10] text-[#F5F5F7]" data-testid="geo-dominance-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#1F2833]">
        <div className="absolute inset-0 te-grid-bg" aria-hidden />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-400/10 blur-[140px]"
          aria-hidden
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.8, ease: "easeOut", delay: 0.4 }}
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#FF3355]/5 blur-[120px]"
          aria-hidden
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-24 lg:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-8 flex items-center gap-3"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot"></span>
            // GEO.DOMINANCE · The 90-Day AI Citation Engine
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid="geo-headline"
            className="font-display text-4xl sm:text-6xl lg:text-8xl font-bold leading-[0.95] tracking-tighter max-w-5xl"
          >
            When AI answers the question, <br className="hidden sm:block" />
            <span className="text-emerald-400">your brand</span> is the answer<span className="text-[#FF3355]">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 text-base sm:text-lg text-[#C5C6C7] max-w-2xl leading-relaxed"
          >
            Strategic Generative Engine Optimization for premium wellness, longevity, and D2C brands. Establish your
            brand as an indisputable category authority — cited, recommended, and linked across ChatGPT, Claude,
            Gemini, and Perplexity within <span className="text-emerald-400 font-bold">90 days</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link
              to="/terminal?tier=geo-authority"
              data-testid="geo-cta-primary"
              className="group inline-flex items-center gap-2 bg-emerald-400 text-[#0B0C10] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-emerald-300 transition-all"
            >
              [ Book GEO Diagnostic ]
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <a
              href="#tiers"
              data-testid="geo-cta-secondary"
              className="inline-flex items-center gap-2 border border-emerald-400/60 text-[#F5F5F7] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-emerald-400/10 hover:border-emerald-400 transition-all"
            >
              See The Tiers
            </a>
          </motion.div>

          {/* Engine strip */}
          <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F2833] border border-[#1F2833] max-w-4xl">
            {engines.map((e, i) => (
              <motion.div
                key={e.name}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + i * 0.08 }}
                className="bg-[#0B0C10] p-6"
              >
                <div className="font-display text-xl text-emerald-400 font-bold">{e.name}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7]">
                  {e.org}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE PROBLEM ============ */}
      <section className="relative py-20 lg:py-28 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// THE.PROBLEM</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              You&apos;re winning on Google. <br />
              <span className="text-[#FF3355]">Losing on AI.</span>
            </h2>
            <p className="mt-6 text-[#C5C6C7] leading-relaxed">
              Every day, 700M+ prompts ask an AI model &ldquo;what&apos;s the best...&rdquo;. If ChatGPT recommends your
              competitor and you&apos;re not even mentioned, that lead never sees your ads, your SEO, or your funnel. AI
              search is a new distribution layer — and category leaders are already engineering their citations.
            </p>
            <p className="mt-4 text-[#C5C6C7] leading-relaxed">
              We&apos;ve reverse-engineered how the four dominant LLMs choose what to cite. GEO Dominance is the
              productized system to move your brand from invisible → cited → recommended → default answer.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {[
              { Icon: Search, k: "50+", label: "Prompt baseline audit" },
              { Icon: Target, k: "4", label: "Engines: GPT · Claude · Gemini · Perplexity" },
              { Icon: Activity, k: "90d", label: "First scorecard delivery window" },
              { Icon: ShieldCheck, k: "48h", label: "Hallucination rapid-response SLA (Shield)" },
            ].map(({ Icon, k, label }, i) => (
              <div key={i} className="te-lime-accent bg-[#0d1117] border border-[#1F2833] p-5">
                <Icon size={16} className="text-emerald-400" />
                <div className="mt-3 font-display text-3xl font-bold text-[#F5F5F7]">{k}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7]">
                  {label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============ 5-STAGE PROCESS ============ */}
      <section className="relative py-20 lg:py-28 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
              // THE.PROCESS
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
              90 days. Five stages. <span className="text-emerald-400">One scorecard.</span>
            </h2>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {stages.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`geo-stage-${s.n}`}
                className="bg-[#0d1117] border border-[#1F2833] p-5 hover:border-emerald-400/60 transition-colors"
              >
                <div className="font-mono text-[10px] text-emerald-400 uppercase tracking-[0.22em]">
                  Stage {s.n}
                </div>
                <div className="mt-3 font-display text-lg font-bold tracking-tight">{s.title}</div>
                <p className="mt-2 text-xs text-[#C5C6C7] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TIERS ============ */}
      <section
        id="tiers"
        className="relative py-20 lg:py-28 border-b border-[#1F2833]"
        data-testid="geo-tiers"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">
              // PRICING.TIERS
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
              Sprint. Dominate. <span className="text-emerald-400">Defend.</span>
            </h2>
            <p className="mt-4 text-[#C5C6C7] max-w-2xl">
              Two one-time 90-day sprints and an ongoing defense retainer. Every tier includes hard-evidence reporting
              — not vibes.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {geoSlugs.map((slug, i) => {
              const svc = servicesContent[slug];
              if (!svc) return null;
              return (
                <motion.div
                  key={slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`geo-tier-card-${slug}`}
                  className={`relative bg-[#0d1117] border p-6 lg:p-8 flex flex-col ${
                    svc.popular
                      ? "border-emerald-400 shadow-[0_0_40px_-10px_rgba(0,255,102,0.35)]"
                      : "border-[#1F2833]"
                  }`}
                >
                  {svc.popular && (
                    <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-emerald-400 text-[#0B0C10] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] font-bold">
                      <Star size={10} fill="currentColor" /> Most Popular
                    </div>
                  )}
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                    {svc.kicker}
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{svc.name}</h3>
                  <div className="mt-1 text-xs text-[#C5C6C7]">{svc.subtitle}</div>

                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="font-display text-4xl font-bold text-[#FF3355]">{svc.priceLabel}</span>
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-[#C5C6C7]/70">{svc.priceNote}</div>

                  <ul className="mt-6 space-y-2 flex-1">
                    {(svc.deliverables || []).slice(0, 5).map((d, idx) => (
                      <li
                        key={idx}
                        className="te-lime-accent flex items-start gap-2 text-xs text-[#C5C6C7]"
                      >
                        <Check size={12} className="text-emerald-400 mt-0.5 shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex flex-col gap-2">
                    <Link
                      to={`/services/${slug}`}
                      data-testid={`geo-tier-details-${slug}`}
                      className="inline-flex items-center justify-center gap-2 border border-emerald-400/60 text-emerald-400 hover:bg-emerald-400/10 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
                    >
                      Full Scope <ArrowUpRight size={12} />
                    </Link>
                    <Link
                      to={svc.ctaHref || `/terminal?tier=${slug}`}
                      data-testid={`geo-tier-cta-${slug}`}
                      className="inline-flex items-center justify-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors"
                    >
                      <Zap size={12} /> {svc.cta || "Get Started"}
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6"
          >
            // START.SHIPPING
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter max-w-4xl mx-auto"
          >
            The AI is answering. <br />
            <span className="text-emerald-400">Make sure it&apos;s saying your name.</span>
          </motion.h2>
          <Link
            to="/terminal?tier=geo-authority"
            data-testid="geo-bottom-cta"
            className="mt-12 inline-flex items-center gap-3 bg-emerald-400 text-[#0B0C10] px-8 py-5 font-mono text-sm uppercase tracking-[0.22em] font-bold hover:bg-emerald-300 transition-all"
          >
            [ Book GEO Diagnostic ]
            <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
